import { type AircraftClassDef, StandKind, type StandKindId } from './airlines';
import {
  BUILDINGS,
  type Building,
  BuildingType,
  buildingDef,
  contactTerminal,
  footprint,
  isRunway,
  runwayGrade,
  runwayLights,
  runwaySize,
  Size,
  type SizeId,
  sizeOf,
} from './buildings';
import { NETWORKS } from './catalog';
import {
  type Ground,
  isTaxiwayRoadTunnel,
  isTaxiwayTunnel,
  NETWORK_CENTERLINE,
  NETWORK_KIND_MASK,
  NETWORK_TOLL,
  NetworkKind,
  roadTunnelAxes,
  roadTunnelLink,
} from './ground';
import { GSE_TICKS_PER_TILE, SERVICE_TICKS_PER_TILE } from './handling';
import {
  airportSharePercent,
  findJunctionBoxes,
  JUNCTION_CARS_PER_MINUTE,
  junctionBits,
} from './junctions';
import { ServiceGraph } from './service-graph';

/** How many cars a minute reach the terminal, and the junction that holds them to it. */
export interface LandsideCapacity {
  /** Cars a minute, or -1 when no junction limits it (or no terminal is reached at all). */
  cars: number;
  /** A tile of the narrowest junction on the way in, or -1. */
  tile: number;
}

/** A junction on the way in to a terminal: its kind, a tile of it, whether it is on the motorway. */
interface RouteJunction {
  kind: number;
  tile: number;
  motorway: boolean;
  /** The junction box (see junctionBox): the same box on two routes is one way in. */
  box: number;
}

/**
 * How far (in tiles) a runway or stand edge looks outward for a taxi centre line. A taxiway painted
 * flush against the edge has its centre line this many tiles away, so it still connects.
 */
const TAXI_REACH = Math.floor(((NETWORKS[NetworkKind.Taxiway]?.width ?? 1) + 1) / 2);

/**
 * Runway indices count along the direction in use (flow): index 0 is the threshold aircraft
 * land over and start their take-off run from.
 * Landing roll: aircraft touch down at this runway index and exit at or after MIN_EXIT.
 */
export const TOUCHDOWN_INDEX = 6;
export const MIN_EXIT_INDEX = 18;
/** Departures need at least this much runway ahead of them. */
export const MAX_ENTRY_INDEX = 34;
export const RUNWAY_LENGTH = BUILDINGS[BuildingType.Runway].width;

export interface RunwayInfo {
  readonly id: number;
  /** Which aircraft it takes, as the original runway's levels go (see runwayGrade). */
  readonly level: number;
  /** What it is equipped with: lights from 2, ILS to CAT III at 3 (see runwayLights). */
  readonly lights: number;
  /** Small, medium or large (see Size), and kept for the big aircraft. */
  readonly size: SizeId;
  readonly bigOnly: boolean;
  readonly rot: number;
  /** Centre-line tile index for every position along the runway (flow 0). */
  readonly tiles: readonly number[];
  /** Taxiway centre-line tile touching the runway edge at each position (flow 0), or -1. */
  readonly access: readonly number[];
  /** The same, counted from the other end (flow 1). */
  readonly accessReversed: readonly number[];
}

/** Taxi access per runway index for the given direction of use. */
export function runwayAccess(rw: RunwayInfo, flow: number): readonly number[] {
  return flow === 0 ? rw.access : rw.accessReversed;
}

/**
 * Buildings the airport's vehicles pass through between the landside and the airside: a
 * terminal and the airside's bases have a gate on each side, so a service road behind one is
 * reached by the road in front of it (as at a real airport, where the apron's service roads
 * start behind the terminal and the handling sheds, not at the motorway).
 */
export const VEHICLE_GATEWAYS: ReadonlySet<number> = new Set<number>([
  BuildingType.Terminal,
  BuildingType.GroundHandling,
  BuildingType.FuelDepot,
  BuildingType.CateringKitchen,
  BuildingType.FireStation,
  BuildingType.Hangar,
]);

/** What serviceReachOf needs to know of a building. */
export interface GatewayCandidate {
  type: number;
  x: number;
  y: number;
  rot: number;
  level: number;
}

/**
 * Road and service road tiles the airport's vehicles reach from the motorway (1): over roads,
 * the motorway and service roads, and through a finished gateway building (VEHICLE_GATEWAYS)
 * once a reached road touches it. Shared by the simulation and the client's building hints.
 */
export function serviceReachOf(
  width: number,
  height: number,
  network: Uint8Array,
  buildings: readonly GatewayCandidate[],
): Uint8Array {
  const graph = new ServiceGraph(network, width, height);
  const size = graph.size;
  // Per node (a tile, or the tunnel under a road, see ServiceGraph): reached or not.
  const nodes = new Uint8Array(size * 2);
  const queue = new Int32Array(size * 2);
  let head = 0;
  let tail = 0;
  const reach = (n: number) => {
    if (nodes[n] === 0 && (n >= size || graph.drivable(n))) {
      nodes[n] = 1;
      queue[tail++] = n;
    }
  };
  const steps: number[] = [];
  const flood = () => {
    while (head < tail) {
      graph.next(queue[head++] as number, steps);
      for (const n of steps) reach(n);
    }
  };
  // A tunnel is driven through, not stopped in: reached (2), but no building beside it counts
  // as reached by it (on a road with a tunnel under it, the road on top does).
  const reached = new Uint8Array(size);
  const tileReach = (t: number) => {
    if (nodes[t] === 1) {
      const v = network[t] as number;
      return isTaxiwayTunnel(v) || isTaxiwayRoadTunnel(v) ? 2 : 1;
    }
    return nodes[size + t] === 1 ? 2 : 0;
  };
  for (let t = 0; t < network.length; t++) {
    if (((network[t] as number) & NETWORK_KIND_MASK) === NetworkKind.Highway) reach(t);
  }
  flood();
  // Through the gateways: once a tile of the ring round one is reached, all of them are.
  const rings = gatewayRings(width, height, buildings);
  const open = new Uint8Array(rings.length);
  for (let changed = true; changed; ) {
    changed = false;
    for (let i = 0; i < rings.length; i++) {
      const ring = rings[i] as number[];
      if (open[i] === 1 || !ring.some((t) => tileReach(t) === 1)) continue;
      open[i] = 1;
      changed = true;
      for (const t of ring) reach(t);
      flood();
    }
  }
  for (let t = 0; t < size; t++) reached[t] = tileReach(t);
  return reached;
}

/**
 * Stands that join up into one apron when they touch edge to edge: the vehicles at one drive
 * on to the next over the concrete, so one service road serves them all.
 */
export const APRON_STANDS: ReadonlySet<number> = new Set<number>([
  BuildingType.Stand,
  BuildingType.ContactStand,
  BuildingType.RemoteParking,
  BuildingType.SmallStand,
  BuildingType.LargeStand,
  BuildingType.SmallParking,
  BuildingType.LargeParking,
]);

/** The stand types airliners park on (every size; contact stands apart). */
export const CIVIL_STANDS: ReadonlySet<number> = new Set<number>([
  BuildingType.Stand,
  BuildingType.SmallStand,
  BuildingType.LargeStand,
]);

/** The remote parking position types (every size). */
export const PARKING_TYPES: ReadonlySet<number> = new Set<number>([
  BuildingType.RemoteParking,
  BuildingType.SmallParking,
  BuildingType.LargeParking,
]);

/**
 * Whether a runway takes an aircraft class: big enough for it, equipped for it (the original
 * runway's levels: regional jets on a first one, narrowbodies from level 2, widebodies from
 * level 3), and not kept for the big aircraft unless it is one.
 */
export function runwayTakes(
  rw: Pick<RunwayInfo, 'size' | 'bigOnly' | 'level'>,
  def: AircraftClassDef,
): boolean {
  return rw.size >= def.size && (!rw.bigOnly || def.big) && rw.level >= def.runwayLevel;
}

/**
 * Whether a stand (or parking position) takes an aircraft class: big enough (a widebody wants a
 * large one), and not kept for the big aircraft unless it is one; a medium stand by its level.
 * `squeeze`: an older airport whose widebodies still use level 2 medium stands (until it
 * builds a large one; World.legacyWideStands).
 */
export function standTakes(
  s: Pick<StandInfo, 'size' | 'bigOnly' | 'level' | 'kind'>,
  def: AircraftClassDef,
  squeeze = false,
): boolean {
  const need = squeeze ? def.size : def.standSize;
  if (s.size < need || (s.bigOnly && !def.big)) return false;
  return s.size !== Size.Medium || s.kind !== StandKind.Civil || s.level >= def.standLevel;
}

/**
 * How well a stand's size suits a class (0 best): a small aircraft on a small stand, the big
 * ones on a large one (a widebody may make do with a medium one), the rest on a medium one.
 */
export function standFit(size: SizeId, def: AircraftClassDef): number {
  if (def.big) return size === Size.Large ? 0 : 1;
  return Math.abs(size - def.size);
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function rectOf(b: GatewayCandidate): Rect | null {
  const def = buildingDef(b.type);
  if (!def) return null;
  const { w, h } = footprint(def, b.rot);
  return { x: b.x, y: b.y, w, h };
}

/** Whether two footprints share a stretch of edge (corners alone do not join them). */
function sideBySide(a: Rect, b: Rect): boolean {
  const alongX = a.x < b.x + b.w && b.x < a.x + a.w;
  const alongY = a.y < b.y + b.h && b.y < a.y + a.h;
  return (
    (alongY && (a.x + a.w === b.x || b.x + b.w === a.x)) ||
    (alongX && (a.y + a.h === b.y || b.y + b.h === a.y))
  );
}

/**
 * The finished stands on one apron with `b` (see APRON_STANDS): joined to it edge to edge,
 * directly or through one another; not `b` itself. None for a building that is no stand.
 */
export function apronNeighbours<T extends GatewayCandidate>(
  b: GatewayCandidate,
  buildings: readonly T[],
): T[] {
  const start = rectOf(b);
  if (!start || !APRON_STANDS.has(b.type)) return [];
  const pool = buildings.filter(
    (o) =>
      o.level >= 1 &&
      APRON_STANDS.has(o.type) &&
      !(o.x === b.x && o.y === b.y && o.type === b.type),
  );
  const rects = pool.map(rectOf);
  const joined = new Uint8Array(pool.length);
  const out: T[] = [];
  const queue: Rect[] = [start];
  for (let head = 0; head < queue.length; head++) {
    const r = queue[head] as Rect;
    for (let i = 0; i < pool.length; i++) {
      const other = rects[i];
      if (joined[i] === 1 || !other || !sideBySide(r, other)) continue;
      joined[i] = 1;
      out.push(pool[i] as T);
      queue.push(other);
    }
  }
  return out;
}

/** The ring of tiles round each finished gateway building (see VEHICLE_GATEWAYS). */
export function gatewayRings(
  width: number,
  height: number,
  buildings: readonly GatewayCandidate[],
): number[][] {
  const rings: number[][] = [];
  for (const b of buildings) {
    if (b.level < 1 || !VEHICLE_GATEWAYS.has(b.type)) continue;
    const def = buildingDef(b.type);
    if (!def) continue;
    const { w, h } = footprint(def, b.rot);
    const ring: number[] = [];
    const add = (x: number, y: number) => {
      if (x >= 0 && y >= 0 && x < width && y < height) ring.push(y * width + x);
    };
    for (let x = b.x; x < b.x + w; x++) {
      add(x, b.y - 1);
      add(x, b.y + h);
    }
    for (let y = b.y; y < b.y + h; y++) {
      add(b.x - 1, y);
      add(b.x + w, y);
    }
    rings.push(ring);
  }
  return rings;
}

export interface StandInfo {
  readonly id: number;
  readonly level: number;
  /** Small, medium or large (see Size), and kept for the big aircraft. */
  readonly size: SizeId;
  readonly bigOnly: boolean;
  /** Who parks here: airliners on stands, fighters in shelters, the rest on military aprons. */
  readonly kind: StandKindId;
  /** A contact stand: passengers board by jet bridge. */
  readonly bridge: boolean;
  /** Taxiway centre-line tiles touching the stand footprint. */
  readonly entries: readonly number[];
  /** Where the aircraft parks (tile index). */
  readonly park: number;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * Taxi network derived from the map: taxiway centre lines are the nodes aircraft move along,
 * runways and stands connect to the nodes that touch them. Rebuilt whenever roads, taxiways or
 * buildings change. Pure derived data: never saved or hashed, identical on every peer.
 */
export class Airfield {
  readonly runways: RunwayInfo[] = [];
  readonly stands: StandInfo[] = [];
  private readonly node: Uint8Array;
  private readonly distances = new Map<number, Int32Array>();
  /** Road tiles reachable from the motorway (1), computed on first use. */
  private landside: Uint8Array | null = null;
  /** Road and service road tiles reachable from the motorway (1). */
  private serviceSide: Uint8Array | null = null;
  /** Service vehicles' travel times (ticks) from a building's doorstep (or the motorway, 0). */
  private readonly serviceFields = new Map<number, Int32Array>();
  /** Their way over the network (tunnels under roads apart from the roads), made on first use. */
  private graph: ServiceGraph | null = null;
  /** Road distance fields (tiles) from a building's doorstep, or from the motorway (key 0). */
  private readonly roadFields = new Map<number, Int32Array>();
  /** Junction boxes (tile → box, -1) and each box's cars a minute, computed on first use. */
  private junctionBoxes: Int32Array | null = null;
  private readonly junctionKinds = new Map<number, number>();
  /** Per connected terminal, the junctions on its way in from the motorway. */
  private landsideRoutes: RouteJunction[][] | null = null;
  /** The ways in from the motorway (see waysIn). */
  private landsideWays: { kind: number; tile: number }[] | null = null;
  /** Per connected terminal (as landsideRoutes): whether its way in passes a toll plaza. */
  private landsideTolled: boolean[] = [];
  /** The roads' tunnels under taxiways (see roadTunnelAxes), found on first use. */
  private roadAxes: Int8Array | null | undefined = undefined;

  constructor(
    private readonly ground: Ground,
    private readonly buildings: readonly Building[],
  ) {
    const g = ground;
    this.node = new Uint8Array(g.width * g.height);
    for (let i = 0; i < g.network.length; i++) {
      const v = g.network[i] as number;
      if ((v & NETWORK_KIND_MASK) === NetworkKind.Taxiway && (v & NETWORK_CENTERLINE) !== 0) {
        this.node[i] = 1;
      }
    }

    for (const b of buildings) {
      if (b.level < 1) continue;
      if (isRunway(b.type)) this.runways.push(this.describeRunway(b));
      if (CIVIL_STANDS.has(b.type)) this.stands.push(this.describeStand(b, StandKind.Civil));
      if (b.type === BuildingType.ContactStand) {
        // Only while its terminal is open (passengers come through it).
        const at = (x: number, y: number) => (g.inBounds(x, y) ? g.occupantAt(x, y) : 0);
        const id = contactTerminal(buildings, at, b.x, b.y, b.rot);
        const terminal = buildings.find((t) => t.id === id);
        if (terminal && terminal.level >= 1)
          this.stands.push(this.describeStand(b, StandKind.Civil));
      }
      if (b.type === BuildingType.DeicePad || b.type === BuildingType.LargeDeicePad) {
        this.stands.push(this.describeStand(b, StandKind.Pad));
      }
      if (PARKING_TYPES.has(b.type)) this.stands.push(this.describeStand(b, StandKind.Remote));
      if (b.type === BuildingType.Shelter)
        this.stands.push(this.describeStand(b, StandKind.Shelter));
      if (b.type === BuildingType.MilitaryApron) {
        this.stands.push(this.describeStand(b, StandKind.Military));
      }
    }
  }

  isNode(tile: number): boolean {
    return this.node[tile] === 1;
  }

  /**
   * Whether a building has road access: a road (or the motorway itself) touching its footprint
   * that leads to the motorway. Passengers and cars only reach connected terminals and car parks.
   */
  landsideConnected(b: Pick<Building, 'type' | 'x' | 'y' | 'rot'>): boolean {
    const reached = this.landsideReach();
    const g = this.ground;
    const def = BUILDINGS[b.type];
    const { w, h } = footprint(def, b.rot);
    // Not a road's tunnel under a taxiway beside it: no building is reached through its wall.
    const at = (x: number, y: number) =>
      g.inBounds(x, y) && reached[g.index(x, y)] === 1 && !this.inTunnel(g.index(x, y));
    for (let x = b.x; x < b.x + w; x++) if (at(x, b.y - 1) || at(x, b.y + h)) return true;
    for (let y = b.y; y < b.y + h; y++) if (at(b.x - 1, y) || at(b.x + w, y)) return true;
    return false;
  }

  /**
   * Whether the airport's own vehicles reach a building: a road or service road touching its
   * footprint that leads to the motorway (stands, the ground handling base, the fuel depot...).
   */
  serviceConnected(b: Pick<Building, 'type' | 'x' | 'y' | 'rot'>): boolean {
    if (this.serviceTouched(b)) return true;
    // A stand on one apron with another that has a road: its vehicles drive over.
    return apronNeighbours({ ...b, level: 1 }, this.buildings).some((n) => this.serviceTouched(n));
  }

  /** Whether a road or service road the vehicles reach touches the building itself. */
  private serviceTouched(b: Pick<Building, 'type' | 'x' | 'y' | 'rot'>): boolean {
    const reached = this.serviceReach();
    const g = this.ground;
    const def = BUILDINGS[b.type];
    const { w, h } = footprint(def, b.rot);
    const at = (x: number, y: number) => g.inBounds(x, y) && reached[g.index(x, y)] === 1;
    for (let x = b.x; x < b.x + w; x++) if (at(x, b.y - 1) || at(x, b.y + h)) return true;
    for (let y = b.y; y < b.y + h; y++) if (at(b.x - 1, y) || at(b.x + w, y)) return true;
    return false;
  }

  /**
   * Ticks a service vehicle takes from a building's doorstep (or from the motorway, when `from`
   * is null) to a road or service road touching `to`, the quickest way: service roads are
   * quicker than the public roads. -1 when there is no way.
   */
  serviceTicks(from: Building | null, to: Pick<Building, 'type' | 'x' | 'y' | 'rot'>): number {
    const own = this.serviceTicksTo(from, to);
    if (own >= 0) return own;
    // No road of its own: by way of the stand beside it on the same apron.
    let best = -1;
    for (const n of apronNeighbours({ ...to, level: 1 }, this.buildings)) {
      const d = this.serviceTicksTo(from, n);
      if (d >= 0 && (best < 0 || d < best)) best = d;
    }
    return best;
  }

  private serviceTicksTo(
    from: Building | null,
    to: Pick<Building, 'type' | 'x' | 'y' | 'rot'>,
  ): number {
    const field = this.serviceField(from);
    const g = this.ground;
    const { w, h } = footprint(BUILDINGS[to.type], to.rot);
    let best = -1;
    const consider = (x: number, y: number) => {
      if (!g.inBounds(x, y) || this.inTunnel(g.index(x, y))) return;
      const d = field[g.index(x, y)] as number;
      if (d >= 0 && (best < 0 || d < best)) best = d;
    };
    for (let x = to.x; x < to.x + w; x++) {
      consider(x, to.y - 1);
      consider(x, to.y + h);
    }
    for (let y = to.y; y < to.y + h; y++) {
      consider(to.x - 1, y);
      consider(to.x + w, y);
    }
    return best;
  }

  /** Dial's algorithm over roads and service roads: ticks to every tile from the seeds. */
  private serviceField(from: Building | null): Int32Array {
    const key = from ? from.id : 0;
    let field = this.serviceFields.get(key);
    if (field) return field;
    const g = this.ground;
    this.graph ??= new ServiceGraph(g.network, g.width, g.height);
    const graph = this.graph;
    // Per node (see ServiceGraph): the tiles first, then the tunnels under roads.
    const out = new Int32Array(graph.size * 2).fill(-1);
    field = out;
    const cost = (n: number) => {
      if (n < graph.size && !graph.drivable(n)) return 0;
      return graph.serviceRoad(n) ? SERVICE_TICKS_PER_TILE : GSE_TICKS_PER_TILE;
    };
    // Buckets by distance, round robin (no step costs more than the largest tile cost).
    const span = Math.max(GSE_TICKS_PER_TILE, SERVICE_TICKS_PER_TILE) + 1;
    const buckets: number[][] = Array.from({ length: span }, () => []);
    let pending = 0;
    const push = (t: number, d: number) => {
      out[t] = d;
      (buckets[d % span] as number[]).push(t);
      pending++;
    };
    // Not from inside a tunnel (no building is reached through its wall).
    const seed = (t: number) => {
      if (out[t] === -1 && cost(t) > 0 && !this.inTunnel(t)) push(t, 0);
    };
    if (from) {
      const { w, h } = footprint(BUILDINGS[from.type], from.rot);
      for (let x = from.x; x < from.x + w; x++) {
        if (g.inBounds(x, from.y - 1)) seed(g.index(x, from.y - 1));
        if (g.inBounds(x, from.y + h)) seed(g.index(x, from.y + h));
      }
      for (let y = from.y; y < from.y + h; y++) {
        if (g.inBounds(from.x - 1, y)) seed(g.index(from.x - 1, y));
        if (g.inBounds(from.x + w, y)) seed(g.index(from.x + w, y));
      }
    } else {
      for (let t = 0; t < g.network.length; t++) {
        if (((g.network[t] as number) & NETWORK_KIND_MASK) === NetworkKind.Highway) seed(t);
      }
    }
    // Through a gateway building: from any tile of the ring round it to the others.
    const rings = gatewayRings(g.width, g.height, this.buildings);
    const ringsAt = new Map<number, number[]>();
    rings.forEach((ring, i) => {
      for (const t of ring) {
        const list = ringsAt.get(t);
        if (list) list.push(i);
        else ringsAt.set(t, [i]);
      }
    });
    const crossed = new Uint8Array(rings.length);
    const steps: number[] = [];
    const step = (n: number, d: number) => {
      const c = cost(n);
      if (c === 0) return;
      const nd = d + c;
      if (out[n] === -1 || nd < (out[n] as number)) push(n, nd);
    };
    for (let d = 0; pending > 0; d++) {
      const bucket = buckets[d % span] as number[];
      // Taken in the order they came: the same on every machine.
      for (let i = 0; i < bucket.length; i++) {
        const t = bucket[i] as number;
        pending--;
        if (out[t] !== d) continue; // superseded by a shorter way
        graph.next(t, steps);
        for (const n of steps) step(n, d);
        for (const r of ringsAt.get(t) ?? []) {
          if (crossed[r] === 1) continue;
          crossed[r] = 1;
          for (const n of rings[r] as number[]) step(n, d);
        }
      }
      bucket.length = 0;
    }
    this.serviceFields.set(key, out);
    return out;
  }

  /**
   * Driving distance in tiles over roads (and the motorway) from a building's doorstep, or from
   * the motorway when `from` is null, to a road touching `to`. -1 when there is no road between.
   * Ground vehicles drive this far from their base (or from outside the airport) to a stand.
   */
  roadDistance(from: Building | null, to: Pick<Building, 'type' | 'x' | 'y' | 'rot'>): number {
    const field = this.roadField(from);
    const g = this.ground;
    const { w, h } = footprint(BUILDINGS[to.type], to.rot);
    let best = -1;
    const consider = (x: number, y: number) => {
      if (!g.inBounds(x, y) || this.inTunnel(g.index(x, y))) return;
      const d = field[g.index(x, y)] as number;
      if (d >= 0 && (best < 0 || d < best)) best = d;
    };
    for (let x = to.x; x < to.x + w; x++) {
      consider(x, to.y - 1);
      consider(x, to.y + h);
    }
    for (let y = to.y; y < to.y + h; y++) {
      consider(to.x - 1, y);
      consider(to.x + w, y);
    }
    return best;
  }

  /**
   * Cars a minute that can reach the terminals from the motorway while `through` cars a minute
   * pass on it. Every way in (a junction where the airport's roads meet the motorway, see
   * waysIn) brings its own flow, its share of the motorway's (airportSharePercent), and the
   * ways in add up: a second road in from the motorway takes cars off the first. Inside, the
   * best-served terminal's narrowest junction caps it all (passengers take the quickest way).
   * The tile is the binding bottleneck: where the jam shows.
   */
  landsideCapacity(buildings: readonly Building[], through = 0): LandsideCapacity {
    const routes = this.routesIn(buildings);
    if (routes.length === 0) return { cars: -1, tile: -1 };
    const flow = (kind: number, motorway: boolean) => {
      const full = JUNCTION_CARS_PER_MINUTE[kind] ?? (JUNCTION_CARS_PER_MINUTE[0] as number);
      return motorway ? Math.floor((full * airportSharePercent(kind, through)) / 100) : full;
    };
    // Inside: the best route's narrowest junction off the motorway (none: no limit).
    let inner = Number.POSITIVE_INFINITY;
    let innerTile = -1;
    let bestInner = -1;
    for (const route of routes) {
      let cars = Number.POSITIVE_INFINITY;
      let tile = -1;
      for (const j of route) {
        if (j.motorway) continue;
        const c = flow(j.kind, false);
        if (c < cars) {
          cars = c;
          tile = j.tile;
        }
      }
      if (cars > bestInner) {
        bestInner = cars;
        inner = cars;
        innerTile = tile;
      }
    }
    // The ways in, added up (none: the roads meet the motorway with no junction).
    let ways = 0;
    let narrowest = Number.POSITIVE_INFINITY;
    let wayTile = -1;
    const all = this.waysIn(buildings);
    for (const w of all) {
      const c = flow(w.kind, true);
      ways += c;
      if (c < narrowest) {
        narrowest = c;
        wayTile = w.tile;
      }
    }
    if (all.length === 0) ways = Number.POSITIVE_INFINITY;
    const cars = Math.min(ways, inner);
    if (!Number.isFinite(cars)) return { cars: -1, tile: -1 };
    return { cars, tile: ways <= inner ? wayTile : innerTile };
  }

  /**
   * The ways in: the junctions on the motorway that the terminals' roads reach (over roads,
   * from the terminals out), each once, in tile order: kind and a tile of it.
   */
  waysIn(buildings: readonly Building[]): { kind: number; tile: number }[] {
    if (this.landsideWays) return this.landsideWays;
    const g = this.ground;
    // Roads, and their tunnels under taxiways (driven through, never stopped in).
    const road = (t: number) =>
      ((g.network[t] as number) & NETWORK_KIND_MASK) === NetworkKind.Road ||
      isTaxiwayRoadTunnel(g.network[t] as number);
    const seen = new Uint8Array(g.width * g.height);
    const queue: number[] = [];
    for (const b of buildings) {
      if (b.type !== BuildingType.Terminal || b.level < 1) continue;
      const { w, h } = footprint(BUILDINGS[b.type], b.rot);
      const push = (x: number, y: number) => {
        if (!g.inBounds(x, y)) return;
        const t = g.index(x, y);
        if (road(t) && !this.inTunnel(t) && !seen[t]) {
          seen[t] = 1;
          queue.push(t);
        }
      };
      for (let x = b.x; x < b.x + w; x++) {
        push(x, b.y - 1);
        push(x, b.y + h);
      }
      for (let y = b.y; y < b.y + h; y++) {
        push(b.x - 1, y);
        push(b.x + w, y);
      }
    }
    const found = new Map<number, { kind: number; tile: number }>();
    // A motorway junction's box may hold only the motorway's tiles: met from the road beside it.
    const meet = (t: number) => {
      const box = this.junctionBox(t);
      if (box >= 0 && !found.has(box) && this.onMotorway(box)) {
        found.set(box, { kind: this.junctionKindOf(box), tile: t });
      }
    };
    for (let i = 0; i < queue.length; i++) {
      const t = queue[i] as number;
      meet(t);
      for (const n of this.neighbours(t)) {
        if (seen[n] || !this.carLink(t, n)) continue;
        if (road(n)) {
          seen[n] = 1;
          queue.push(n);
        } else {
          meet(n);
        }
      }
    }
    this.landsideWays = [...found.values()].sort((a, b) => a.tile - b.tile);
    return this.landsideWays;
  }

  /** The junctions on the way in to each connected terminal (down the motorway distances). */
  private routesIn(buildings: readonly Building[]): RouteJunction[][] {
    if (this.landsideRoutes) return this.landsideRoutes;
    const g = this.ground;
    const field = this.roadField(null);
    const routes: RouteJunction[][] = [];
    for (const b of buildings) {
      if (b.type !== BuildingType.Terminal || b.level < 1) continue;
      // The road tile by the terminal nearest the motorway, then down the distance field.
      const { w, h } = footprint(BUILDINGS[b.type], b.rot);
      let at = -1;
      const consider = (x: number, y: number) => {
        if (!g.inBounds(x, y) || this.inTunnel(g.index(x, y))) return;
        const t = g.index(x, y);
        const d = field[t] as number;
        if (d >= 0 && (at < 0 || d < (field[at] as number))) at = t;
      };
      for (let x = b.x; x < b.x + w; x++) {
        consider(x, b.y - 1);
        consider(x, b.y + h);
      }
      for (let y = b.y; y < b.y + h; y++) {
        consider(b.x - 1, y);
        consider(b.x + w, y);
      }
      if (at < 0) continue;
      const route: RouteJunction[] = [];
      const seen = new Set<number>();
      let tolled = false;
      for (let t = at; ; ) {
        const v = g.network[t] as number;
        if ((v & NETWORK_KIND_MASK) === NetworkKind.Road && (v & NETWORK_TOLL) !== 0) tolled = true;
        const box = this.junctionBox(t);
        if (box >= 0 && !seen.has(box)) {
          seen.add(box);
          route.push({
            kind: this.junctionKindOf(box),
            tile: t,
            motorway: this.onMotorway(box),
            box,
          });
        }
        const d = field[t] as number;
        if (d <= 0) break;
        const next = this.neighbours(t).find((n) => field[n] === d - 1 && this.carLink(t, n));
        if (next === undefined) break;
        t = next;
      }
      routes.push(route);
      this.landsideTolled.push(tolled);
    }
    this.landsideRoutes = routes;
    return routes;
  }

  /**
   * Of the terminals reached by road, the share (percent) whose way in from the motorway passes
   * a toll plaza: that share of the passengers pays the toll.
   */
  tolledPercent(buildings: readonly Building[]): number {
    const routes = this.routesIn(buildings);
    if (routes.length === 0) return 0;
    let tolled = 0;
    for (const t of this.landsideTolled) if (t) tolled++;
    return Math.floor((tolled * 100) / routes.length);
  }

  /** The junction box a tile is in, or -1. */
  junctionBox(t: number): number {
    if (!this.junctionBoxes) {
      const g = this.ground;
      this.junctionBoxes = findJunctionBoxes(g.network, g.width, g.height);
    }
    return this.junctionBoxes[t] as number;
  }

  /** A junction box's kind: the highest kind on its tiles. */
  private junctionKindOf(box: number): number {
    let kind = this.junctionKinds.get(box);
    if (kind !== undefined) return kind;
    const boxes = this.junctionBoxes as Int32Array;
    kind = 0;
    for (let t = 0; t < boxes.length; t++) {
      if (boxes[t] === box) kind = Math.max(kind, junctionBits(this.ground.network[t] as number));
    }
    this.junctionKinds.set(box, kind);
    return kind;
  }

  /** Whether a junction box is on the motorway (any of its tiles is). */
  private onMotorway(box: number): boolean {
    const boxes = this.junctionBoxes as Int32Array;
    for (let t = 0; t < boxes.length; t++) {
      if (boxes[t] !== box) continue;
      if (((this.ground.network[t] as number) & NETWORK_KIND_MASK) === NetworkKind.Highway) {
        return true;
      }
    }
    return false;
  }

  private roadField(from: Building | null): Int32Array {
    const key = from ? from.id : 0;
    let field = this.roadFields.get(key);
    if (field) return field;
    const g = this.ground;
    field = new Int32Array(g.width * g.height).fill(-1);
    const queue = new Int32Array(g.width * g.height);
    let head = 0;
    let tail = 0;
    const drivable = (t: number) => this.carTile(t);
    const seed = (t: number) => {
      if (field?.[t] === -1 && drivable(t) && !this.inTunnel(t)) {
        field[t] = 0;
        queue[tail++] = t;
      }
    };
    if (from) {
      const { w, h } = footprint(BUILDINGS[from.type], from.rot);
      for (let x = from.x; x < from.x + w; x++) {
        if (g.inBounds(x, from.y - 1)) seed(g.index(x, from.y - 1));
        if (g.inBounds(x, from.y + h)) seed(g.index(x, from.y + h));
      }
      for (let y = from.y; y < from.y + h; y++) {
        if (g.inBounds(from.x - 1, y)) seed(g.index(from.x - 1, y));
        if (g.inBounds(from.x + w, y)) seed(g.index(from.x + w, y));
      }
    } else {
      for (let t = 0; t < g.network.length; t++) {
        if (((g.network[t] as number) & NETWORK_KIND_MASK) === NetworkKind.Highway) seed(t);
      }
    }
    while (head < tail) {
      const t = queue[head++] as number;
      const d = (field[t] as number) + 1;
      for (const n of this.neighbours(t)) {
        if (field[n] === -1 && drivable(n) && this.carLink(t, n)) {
          field[n] = d;
          queue[tail++] = n;
        }
      }
    }
    this.roadFields.set(key, field);
    return field;
  }

  /** Roads and service roads the airport's vehicles reach (see serviceReachOf). */
  private serviceReach(): Uint8Array {
    if (this.serviceSide) return this.serviceSide;
    const g = this.ground;
    this.serviceSide = serviceReachOf(g.width, g.height, g.network, this.buildings);
    return this.serviceSide;
  }

  /** Whether cars drive on (or through) a tile: a road, the motorway, a road's tunnel. */
  private carTile(t: number): boolean {
    const v = this.ground.network[t] as number;
    const kind = v & NETWORK_KIND_MASK;
    return kind === NetworkKind.Road || kind === NetworkKind.Highway || isTaxiwayRoadTunnel(v);
  }

  /** Whether a tile is a tunnel under a taxiway (a service road's or a road's): driven through. */
  private inTunnel(t: number): boolean {
    const v = this.ground.network[t] as number;
    return isTaxiwayTunnel(v) || isTaxiwayRoadTunnel(v);
  }

  /** Whether cars may step from a tile to a neighbour (see roadTunnelLink). */
  private carLink(a: number, b: number): boolean {
    if (this.roadAxes === undefined) {
      const g = this.ground;
      this.roadAxes = roadTunnelAxes(g.network, g.width, g.height);
    }
    return roadTunnelLink(this.roadAxes, a, b, b === a + 1 || b === a - 1 ? 0 : 1);
  }

  /** Flood fill over roads from every motorway tile. */
  private landsideReach(): Uint8Array {
    if (this.landside) return this.landside;
    const g = this.ground;
    const reached = new Uint8Array(g.width * g.height);
    const queue = new Int32Array(g.width * g.height);
    let head = 0;
    let tail = 0;
    const isRoad = (t: number) => this.carTile(t);
    for (let t = 0; t < g.network.length; t++) {
      if (((g.network[t] as number) & NETWORK_KIND_MASK) === NetworkKind.Highway) {
        reached[t] = 1;
        queue[tail++] = t;
      }
    }
    while (head < tail) {
      const t = queue[head++] as number;
      for (const n of this.neighbours(t)) {
        if (reached[n] === 0 && isRoad(n) && this.carLink(t, n)) {
          reached[n] = 1;
          queue[tail++] = n;
        }
      }
    }
    this.landside = reached;
    return reached;
  }

  /**
   * Whether aircraft can taxi between a runway and the parking place (stand, contact stand,
   * remote position, de-icing pad, shelter or military apron) with this building id.
   */
  standLinked(id: number): boolean {
    const stand = this.stands.find((s) => s.id === id);
    if (!stand) return false;
    const dist = this.distancesTo(stand);
    return this.runways.some((rw) => rw.access.some((n) => n >= 0 && (dist[n] ?? -1) >= 0));
  }

  /** BFS distance (in tiles) from every taxi node to the stand's entries; -1 = unreachable. */
  distancesTo(stand: StandInfo): Int32Array {
    let dist = this.distances.get(stand.id);
    if (dist) return dist;
    const g = this.ground;
    dist = new Int32Array(g.width * g.height).fill(-1);
    const queue = new Int32Array(g.width * g.height);
    let head = 0;
    let tail = 0;
    for (const e of stand.entries) {
      if (dist[e] === -1) {
        dist[e] = 0;
        queue[tail++] = e;
      }
    }
    while (head < tail) {
      const t = queue[head++] as number;
      const d = (dist[t] as number) + 1;
      for (const n of this.neighbours(t)) {
        if (this.node[n] === 1 && dist[n] === -1) {
          dist[n] = d;
          queue[tail++] = n;
        }
      }
    }
    this.distances.set(stand.id, dist);
    return dist;
  }

  /**
   * Tiles from a taxi node to the stand's parking spot: down the BFS gradient to an entry, then
   * straight into the footprint. Null if unreachable.
   */
  routeToStand(from: number, stand: StandInfo): number[] | null {
    const dist = this.distancesTo(stand);
    if ((dist[from] ?? -1) < 0) return null;
    const route = [from];
    let t = from;
    while ((dist[t] as number) > 0) {
      const d = dist[t] as number;
      const next = this.neighbours(t).find((n) => dist[n] === d - 1);
      if (next === undefined) return null;
      route.push(next);
      t = next;
    }
    route.push(...this.approachTiles(t, stand));
    return route;
  }

  /** N, E, S, W neighbours inside the map (fixed order keeps routes deterministic). */
  private neighbours(t: number): number[] {
    const { width: w, height: h } = this.ground;
    const x = t % w;
    const y = (t - x) / w;
    const out: number[] = [];
    if (y > 0) out.push(t - w);
    if (x < w - 1) out.push(t + 1);
    if (y < h - 1) out.push(t + w);
    if (x > 0) out.push(t - 1);
    return out;
  }

  /**
   * First taxi node walking from (x, y) in direction (dx, dy), crossing at most TAXI_REACH taxiway
   * tiles; -1 if the walk leaves the taxiway or the map first.
   */
  private nodeOutward(x: number, y: number, dx: number, dy: number): number {
    const g = this.ground;
    for (let k = 0; k < TAXI_REACH; k++) {
      const tx = x + dx * k;
      const ty = y + dy * k;
      if (!g.inBounds(tx, ty)) return -1;
      const t = g.index(tx, ty);
      if (this.node[t] === 1) return t;
      if (g.networkKindAt(tx, ty) !== NetworkKind.Taxiway) return -1;
    }
    return -1;
  }

  /** Tiles from an entry node (outside the footprint) to the parking tile. */
  private approachTiles(entry: number, s: StandInfo): number[] {
    const w = this.ground.width;
    let x = entry % w;
    let y = (entry - x) / w;
    const px = s.park % w;
    const py = (s.park - px) / w;
    const out: number[] = [];
    const step = (nx: number, ny: number) => {
      x = nx;
      y = ny;
      out.push(y * w + x);
    };
    const vertical = y < s.y || y >= s.y + s.h;
    if (vertical) {
      while (y !== py) step(x, y + Math.sign(py - y));
      while (x !== px) step(x + Math.sign(px - x), y);
    } else {
      while (x !== px) step(x + Math.sign(px - x), y);
      while (y !== py) step(x, y + Math.sign(py - y));
    }
    return out;
  }

  private describeRunway(b: Building): RunwayInfo {
    const w = this.ground.width;
    const tiles: number[] = [];
    const access: number[] = [];
    const along = b.rot % 2 === 0;
    // Its width across (4 for the original, narrower or wider for the other sizes); the
    // centre line on the row just before the middle.
    const across = BUILDINGS[b.type].height;
    const centre = Math.floor((across - 1) / 2);
    for (let i = 0; i < RUNWAY_LENGTH; i++) {
      const [cx, cy] = along ? [b.x + i, b.y + centre] : [b.x + centre, b.y + i];
      tiles.push(cy * w + cx);
      // Taxi nodes touching either long edge of the runway.
      const sides: [number, number, number, number][] = along
        ? [
            [b.x + i, b.y - 1, 0, -1],
            [b.x + i, b.y + across, 0, 1],
          ]
        : [
            [b.x - 1, b.y + i, -1, 0],
            [b.x + across, b.y + i, 1, 0],
          ];
      let found = -1;
      for (const [sx, sy, dx, dy] of sides) {
        found = this.nodeOutward(sx, sy, dx, dy);
        if (found !== -1) break;
      }
      access.push(found);
    }
    return {
      id: b.id,
      level: runwayGrade(b),
      lights: runwayLights(b),
      size: runwaySize(b),
      bigOnly: b.bigOnly === 1,
      rot: b.rot,
      tiles,
      access,
      accessReversed: access.toReversed(),
    };
  }

  private describeStand(b: Building, kind: StandKindId): StandInfo {
    const g = this.ground;
    const def = BUILDINGS[b.type];
    const { w, h } = footprint(def, b.rot);
    const entries: number[] = [];
    const consider = (x: number, y: number, dx: number, dy: number) => {
      const n = this.nodeOutward(x, y, dx, dy);
      if (n !== -1 && !entries.includes(n)) entries.push(n);
    };
    for (let x = b.x; x < b.x + w; x++) {
      consider(x, b.y - 1, 0, -1);
      consider(x, b.y + h, 0, 1);
    }
    for (let y = b.y; y < b.y + h; y++) {
      consider(b.x - 1, y, -1, 0);
      consider(b.x + w, y, 1, 0);
    }
    const park = g.index(b.x + Math.floor(w / 2), b.y + Math.floor(h / 2));
    const bridge = b.type === BuildingType.ContactStand;
    return {
      id: b.id,
      level: b.level,
      size: sizeOf(b.type),
      bigOnly: b.bigOnly === 1,
      kind,
      bridge,
      entries,
      park,
      x: b.x,
      y: b.y,
      w,
      h,
    };
  }
}
